import React, { useState, useEffect, useRef } from 'react';
import { shopLocationService } from '../services/shopLocationService';

interface AuthenticatedLocationImageProps {
  shopId: string;
  locationId: string;
  placeId?: string;
  imageId: string;
  alt: string;
  size?: 'thumb_300' | 'thumb_800';
  style?: React.CSSProperties;
  className?: string;
  /** Called when a HEIC image is detected (pending async conversion).
   *  Parent should periodically re-fetch location data so imageId prop updates
   *  when the conversion completes and the image ID is swapped. */
  onHeicPending?: () => void;
}

/**
 * Displays an authenticated location/place image.
 *
 * - **With Service Worker** (production): renders <img src> — the SW injects
 *   the Bearer token, browser HTTP cache handles caching.
 * - **Without Service Worker** (local dev): falls back to fetch() with auth
 *   headers and blob URLs.
 *
 * Image IDs are immutable (content-addressed). When image content changes
 * (rotation/crop/HEIC conversion), a new image ID is generated, so the URL
 * itself changes and no cache-busting is needed.
 *
 * HEIC handling: When a HEIC file is detected, the component shows a
 * "Processing image" spinner and calls onHeicPending(). The parent is
 * responsible for re-fetching location data — when the Cloud Function converts
 * the HEIC and swaps the image ID, the parent will get the new imageId and
 * pass it as a prop, causing this component to re-render with the PNG.
 */
const AuthenticatedLocationImage: React.FC<AuthenticatedLocationImageProps> = ({
  shopId, locationId, placeId, imageId, alt, size, style, className,
  onHeicPending,
}) => {
  // Decide path once on mount and stick with it — don't switch mid-render.
  const [hasSW] = useState(() => Boolean(navigator.serviceWorker?.controller));

  if (hasSW) {
    return (
      <SwLocationImage
        shopId={shopId} locationId={locationId} placeId={placeId}
        imageId={imageId} alt={alt} size={size} style={style} className={className}
        onHeicPending={onHeicPending}
      />
    );
  }

  return (
    <FetchLocationImage
      shopId={shopId} locationId={locationId} placeId={placeId}
      imageId={imageId} alt={alt} size={size} style={style} className={className}
      onHeicPending={onHeicPending}
    />
  );
};

/** Service Worker path — plain <img src>, SW injects token, browser caches. */
const SwLocationImage: React.FC<AuthenticatedLocationImageProps> = ({
  shopId, locationId, placeId, imageId, alt, size, style, className, onHeicPending,
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHeicPending, setIsHeicPending] = useState(false);
  const onHeicPendingRef = useRef(onHeicPending);
  onHeicPendingRef.current = onHeicPending;

  const imageUrl = placeId
    ? shopLocationService.getPlaceImageUrl(shopId, locationId, placeId, imageId, size)
    : shopLocationService.getLocationImageUrl(shopId, locationId, imageId, size);

  // Reset state when imageId changes (e.g., after HEIC swap)
  useEffect(() => {
    setHasError(false);
    setIsLoaded(false);
    setIsHeicPending(false);
  }, [imageId]);

  const handleError = async () => {
    // Image failed to load — check if it's a HEIC file (browsers can't render HEIC natively)
    try {
      const headResponse = placeId
        ? await shopLocationService.fetchPlaceImageHeaders(shopId, locationId, placeId, imageId, size)
        : await shopLocationService.fetchLocationImageHeaders(shopId, locationId, imageId, size);
      const contentType = headResponse.headers.get('Content-Type') || '';
      const isHeic = contentType.includes('image/heic') || contentType.includes('image/heif');
      if (isHeic) {
        setIsHeicPending(true);
        onHeicPendingRef.current?.();
        return;
      }
    } catch {
      // HEAD request failed — fall through to error state
    }
    setHasError(true);
  };

  if (isHeicPending) {
    return (
      <div className={className} style={{ position: 'relative', ...style, overflow: 'hidden' }}>
        <HeicProcessingSpinner />
      </div>
    );
  }

  if (hasError) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className || ''}`}
        style={style}
      >
        <div className="text-center">
          <div className="text-lg mb-1">&#9888;&#65039;</div>
          <div className="text-xs">Error</div>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={{ position: 'relative', ...style, overflow: 'hidden' }}>
      {!isLoaded && (
        <div
          className="flex items-center justify-center bg-gray-100 text-gray-500"
          style={{ position: 'absolute', inset: 0, zIndex: 1 }}
        >
          <div className="text-center">
            <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mb-1"></div>
            <div className="text-xs">Loading...</div>
          </div>
        </div>
      )}
      <img
        src={imageUrl}
        alt={alt}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: style?.objectFit || 'cover',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.15s ease',
        }}
        onLoad={() => setIsLoaded(true)}
        onError={handleError}
      />
    </div>
  );
};

/** Fallback (no SW) — fetch() with Bearer token + blob URL. */
const FetchLocationImage: React.FC<AuthenticatedLocationImageProps> = ({
  shopId, locationId, placeId, imageId, alt, size, style, className, onHeicPending,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isHeicPending, setIsHeicPending] = useState(false);
  const onHeicPendingRef = useRef(onHeicPending);
  onHeicPendingRef.current = onHeicPending;

  useEffect(() => {
    let isMounted = true;
    let currentBlobUrl: string | null = null;

    const fetchImage = async () => {
      try {
        setHasError(false);
        setIsHeicPending(false);
        setIsLoading(true);

        const response = placeId
          ? await shopLocationService.fetchPlaceImage(shopId, locationId, placeId, imageId, size)
          : await shopLocationService.fetchLocationImage(shopId, locationId, imageId, size);
        if (!isMounted) return;

        if (response.ok) {
          const contentType = response.headers.get('Content-Type') || '';
          const isHeic = contentType.includes('image/heic') || contentType.includes('image/heif');
          if (isHeic) {
            setIsHeicPending(true);
            setIsLoading(false);
            onHeicPendingRef.current?.();
            return;
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
          currentBlobUrl = blobUrl;
          setImageSrc(blobUrl);
          setIsHeicPending(false);
          setIsLoading(false);
        } else {
          setHasError(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch authenticated location/place image:', error);
        if (isMounted) { setHasError(true); setIsLoading(false); }
      }
    };

    fetchImage();
    return () => {
      isMounted = false;
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [shopId, locationId, placeId, imageId, size]);

  return (
    <div className={className} style={{ position: 'relative', ...style, overflow: 'hidden' }}>
      {isHeicPending && <HeicProcessingSpinner />}
      {isLoading && !isHeicPending && (
        <div
          className="flex items-center justify-center bg-gray-100 text-gray-500"
          style={{ position: 'absolute', inset: 0, zIndex: 1 }}
        >
          <div className="text-center">
            <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mb-1"></div>
            <div className="text-xs">Loading...</div>
          </div>
        </div>
      )}
      {(hasError || (!isLoading && !imageSrc && !isHeicPending)) && (
        <div
          className="flex items-center justify-center bg-gray-100 text-gray-400"
          style={{ position: 'absolute', inset: 0, zIndex: 1 }}
        >
          <div className="text-center">
            <div className="text-lg mb-1">&#9888;&#65039;</div>
            <div className="text-xs">Error</div>
          </div>
        </div>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: style?.objectFit || 'cover',
          }}
        />
      )}
    </div>
  );
};

/** Shared HEIC processing spinner shown while Cloud Function converts HEIC to PNG. */
const HeicProcessingSpinner: React.FC = () => (
  <div
    className="flex items-center justify-center"
    style={{ position: 'absolute', inset: 0, zIndex: 1, backgroundColor: 'var(--bg-secondary)' }}
  >
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'inline-block', width: '24px', height: '24px', marginBottom: '8px' }}>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
          style={{ width: '100%', height: '100%', animation: 'spin 1s linear infinite' }}>
          <circle cx="12" cy="12" r="10" stroke="var(--primary-light)" strokeWidth="3" />
          <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--text-accent)" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-accent)' }}>Processing image</div>
    </div>
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </div>
);

export default AuthenticatedLocationImage;
